import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Star, Trash2, Pencil, Flag, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { ratingAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import StarPicker from './StarPicker';
import SellerRatingBadge from './SellerRatingBadge';

const REPORT_REASONS = ['spam', 'harassment', 'false_claim', 'inappropriate', 'other'];

function formatDate(iso) {
	if (!iso) return '';
	try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
}

export default function SellerRatingsSection({ sellerId, sellerName }) {
	const { t } = useTranslation();
	const { user } = useAuth();
	const isOwner = user && user.id === sellerId;

	const [items, setItems] = useState([]);
	const [avg, setAvg] = useState(0);
	const [count, setCount] = useState(0);
	const [loading, setLoading] = useState(true);
	const [myRating, setMyRating] = useState(null);
	const [canRate, setCanRate] = useState(false);

	// Write / edit form state
	const [showForm, setShowForm] = useState(false);
	const [stars, setStars] = useState(0);
	const [comment, setComment] = useState('');
	const [saving, setSaving] = useState(false);

	// Report dialog
	const [reportDlg, setReportDlg] = useState({ open: false, ratingId: null });
	const [reportReason, setReportReason] = useState('spam');
	const [reportNote, setReportNote] = useState('');
	const [reporting, setReporting] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [listRes, myRes] = await Promise.all([
				ratingAPI.listForSeller(sellerId, { limit: 50 }),
				user && !isOwner ? ratingAPI.my(sellerId).catch(() => ({ data: { rating: null, can_rate: false } })) : Promise.resolve({ data: { rating: null, can_rate: false } }),
			]);
			setItems(listRes.data.items || []);
			setAvg(listRes.data.avg || 0);
			setCount(listRes.data.count || 0);
			setMyRating(myRes.data.rating);
			setCanRate(!!myRes.data.can_rate);
		} catch {
			// silent
		} finally {
			setLoading(false);
		}
	}, [sellerId, user, isOwner]);

	useEffect(() => { load(); }, [load]);

	const openWriteForm = () => {
		setStars(myRating?.stars || 0);
		setComment(myRating?.comment || '');
		setShowForm(true);
	};

	const submitRating = async () => {
		if (!stars) { toast.error(t('rating.error_stars_required')); return; }
		setSaving(true);
		try {
			if (myRating) {
				await ratingAPI.update(myRating.id, { stars, comment });
				toast.success(t('rating.updated'));
			} else {
				await ratingAPI.create({ seller_id: sellerId, stars, comment });
				toast.success(t('rating.submitted'));
			}
			setShowForm(false);
			setComment('');
			setStars(0);
			await load();
		} catch (err) {
			const msg = err.response?.data?.detail || t('common.error');
			toast.error(msg);
		} finally {
			setSaving(false);
		}
	};

	const deleteMyRating = async () => {
		if (!myRating) return;
		if (!window.confirm(t('rating.confirm_delete'))) return;
		try {
			await ratingAPI.remove(myRating.id);
			toast.success(t('rating.deleted'));
			await load();
		} catch { toast.error(t('common.error')); }
	};

	const openReport = (ratingId) => {
		setReportDlg({ open: true, ratingId });
		setReportReason('spam');
		setReportNote('');
	};

	const submitReport = async () => {
		setReporting(true);
		try {
			await ratingAPI.report(reportDlg.ratingId, { reason: reportReason, note: reportNote });
			toast.success(t('rating.report_submitted'));
			setReportDlg({ open: false, ratingId: null });
			await load();
		} catch (err) {
			toast.error(err.response?.data?.detail || t('common.error'));
		} finally {
			setReporting(false);
		}
	};

	const canWriteRating = user && !isOwner && (canRate || myRating);

	return (
		<div className="space-y-6" data-testid="seller-ratings-section">
			{/* Summary header */}
			<div className="flex items-center justify-between flex-wrap gap-4 border-b border-[#E6E2D6] pb-4">
				<div className="flex items-center gap-4">
					<div>
						<div className="flex items-center gap-2">
							<span className="text-3xl font-bold text-[#1A3C34] font-['Playfair_Display']" data-testid="seller-rating-avg">{count ? avg.toFixed(1) : '—'}</span>
							<SellerRatingBadge avg={avg} count={count} size="md" showCount={false} />
						</div>
						<p className="text-xs text-[#666055] mt-1" data-testid="seller-rating-count">
							{count === 0 ? t('rating.no_ratings_yet') : t('rating.based_on_count', { count })}
						</p>
					</div>
				</div>
				{canWriteRating && (
					<Button
						onClick={openWriteForm}
						className="bg-[#1A3C34] hover:bg-[#152F29] text-white rounded-sm h-10 btn-equi"
						data-testid="rating-write-btn"
					>
						<Star className="w-4 h-4 mr-2" />
						{myRating ? t('rating.edit_my_rating') : t('rating.write_a_rating')}
					</Button>
				)}
				{user && !isOwner && !canWriteRating && (
					<p className="text-xs text-[#666055] italic max-w-sm text-right" data-testid="rating-cant-rate-hint">
						{t('rating.must_message_first')}
					</p>
				)}
			</div>

			{/* Write/Edit form */}
			{showForm && (
				<div className="bg-[#F9F7F2] border border-[#E6E2D6] rounded-sm p-4 md:p-6 space-y-4" data-testid="rating-form">
					<div>
						<p className="text-xs text-[#666055] uppercase tracking-wider mb-2">{t('rating.your_rating')}</p>
						<StarPicker value={stars} onChange={setStars} />
					</div>
					<div>
						<p className="text-xs text-[#666055] uppercase tracking-wider mb-2">{t('rating.comment_optional')}</p>
						<Textarea
							value={comment}
							onChange={(e) => setComment(e.target.value)}
							placeholder={t('rating.comment_placeholder')}
							rows={4}
							maxLength={1000}
							className="bg-white"
							data-testid="rating-form-comment"
						/>
					</div>
					<div className="flex items-center justify-end gap-2 flex-wrap">
						{myRating && (
							<Button
								variant="outline"
								onClick={deleteMyRating}
								className="border-[#9B2C2C] text-[#9B2C2C] hover:bg-[#FBEAEA] rounded-sm h-10"
								data-testid="rating-form-delete"
							>
								<Trash2 className="w-4 h-4 mr-1" /> {t('rating.delete_my_rating')}
							</Button>
						)}
						<Button variant="ghost" onClick={() => setShowForm(false)} className="text-[#666055] rounded-sm h-10">
							{t('common.cancel')}
						</Button>
						<Button
							onClick={submitRating}
							disabled={saving || !stars}
							className="bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm h-10 btn-equi"
							data-testid="rating-form-submit"
						>
							{saving ? t('common.loading') : (myRating ? t('common.save') : t('rating.submit'))}
						</Button>
					</div>
				</div>
			)}

			{/* Ratings list */}
			{loading ? (
				<div className="text-center py-6 text-sm text-[#666055]">{t('common.loading')}</div>
			) : items.length === 0 ? (
				<div className="text-center py-10 text-sm text-[#666055]" data-testid="rating-empty-state">
					{t('rating.be_first', { name: sellerName })}
				</div>
			) : (
				<div className="space-y-4" data-testid="rating-list">
					{items.map((r) => {
						const isMine = user && r.rater_id === user.id;
						const raterLink = r.rater_slug ? `/u/${r.rater_slug}` : null;
						return (
							<div key={r.id} className="bg-white border border-[#E6E2D6] rounded-sm p-4" data-testid={`rating-item-${r.id}`}>
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<div className="flex items-center gap-2 flex-wrap">
											<StarPicker value={r.stars} onChange={() => {}} size={16} disabled />
											{raterLink ? (
												<Link to={raterLink} className="text-sm font-semibold text-[#1A3C34] hover:text-[#8B4513] truncate">
													{r.rater_name}
												</Link>
											) : (
												<span className="text-sm font-semibold text-[#1A3C34] truncate">{r.rater_name}</span>
											)}
											{isMine && (
												<span className="bg-[#F2F0E9] text-[#666055] px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider">
                          {t('rating.your_review')}
                        </span>
											)}
										</div>
										<p className="text-xs text-[#666055] mt-0.5">{formatDate(r.created_at)}{r.updated_at && r.updated_at !== r.created_at ? ` · ${t('rating.edited')}` : ''}</p>
										{r.comment && (
											<p className="mt-2 text-sm text-[#3a3a3a] whitespace-pre-line" data-testid={`rating-comment-${r.id}`}>{r.comment}</p>
										)}
									</div>
									<div className="flex items-center gap-1 flex-shrink-0">
										{isMine && (
											<button
												onClick={openWriteForm}
												className="p-2 text-[#666055] hover:text-[#8B4513] rounded-sm"
												title={t('rating.edit_my_rating')}
												data-testid={`rating-edit-${r.id}`}
											>
												<Pencil className="w-4 h-4" />
											</button>
										)}
										{isOwner && !isMine && !r.reported && (
											<button
												onClick={() => openReport(r.id)}
												className="p-2 text-[#666055] hover:text-[#9B2C2C] rounded-sm"
												title={t('rating.report_rating')}
												data-testid={`rating-report-${r.id}`}
											>
												<Flag className="w-4 h-4" />
											</button>
										)}
										{isOwner && r.reported && (
											<span className="inline-flex items-center gap-1 text-[10px] text-[#9B2C2C] bg-[#FBEAEA] px-2 py-1 rounded-sm uppercase tracking-wider" title={t('rating.report_pending_hint')}>
                        <ShieldAlert className="w-3 h-3" /> {t('rating.reported')}
                      </span>
										)}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}

			{/* Report dialog */}
			<Dialog open={reportDlg.open} onOpenChange={(o) => !o && setReportDlg({ open: false, ratingId: null })}>
				<DialogContent className="max-w-md" data-testid="rating-report-dialog">
					<DialogHeader>
						<DialogTitle className="font-['Playfair_Display'] text-[#1A3C34]">{t('rating.report_rating')}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 pt-2">
						<p className="text-xs text-[#666055]">{t('rating.report_help')}</p>
						<div>
							<p className="text-xs text-[#666055] uppercase tracking-wider mb-2">{t('rating.report_reason')}</p>
							<Select value={reportReason} onValueChange={setReportReason}>
								<SelectTrigger className="h-10 rounded-sm bg-[#F9F7F2] border-[#E6E2D6]" data-testid="rating-report-reason">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{REPORT_REASONS.map((rr) => (
										<SelectItem key={rr} value={rr}>{t(`rating.report_reason_${rr}`)}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div>
							<p className="text-xs text-[#666055] uppercase tracking-wider mb-2">{t('rating.report_note')}</p>
							<Textarea
								value={reportNote}
								onChange={(e) => setReportNote(e.target.value)}
								placeholder={t('rating.report_note_placeholder')}
								rows={4}
								maxLength={1000}
								className="bg-white"
								data-testid="rating-report-note"
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setReportDlg({ open: false, ratingId: null })} className="text-[#666055]">
							{t('common.cancel')}
						</Button>
						<Button
							onClick={submitReport}
							disabled={reporting}
							className="bg-[#9B2C2C] hover:bg-[#7B1F1F] text-white rounded-sm btn-equi"
							data-testid="rating-report-submit"
						>
							<Flag className="w-4 h-4 mr-1" /> {reporting ? t('common.loading') : t('rating.submit_report')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}